import React, { useState } from "react";
import { Switch, message } from "antd";
import { useAuth0 } from "../../../react-auth0-spa";
import axios from "axios";
import config from "../../../api_config.json";
import { useAppContext } from "../../../context";
import updateOrgDetails from "./updateOrgDetails";

const FieldMarketplace = ({ org }) => {
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(org.marketplace || false);
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();

  const env = isProd ? "prod" : "dev";

  const checkMarketplaceSellerExists = async (orgId) => {
    const token = await getTokenSilently();
    const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

    const query = `
      query CheckMarketplaceSeller($orgId: ID!) {
        marketplace_sellers(where: { org_pointer: { have: { objectId: { equalTo: $orgId } } } }) {
          count
        }
      }
    `;

    try {
      const response = await axios.post(
        graphqlUrl,
        {
          query,
          variables: { orgId },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data.marketplace_sellers.count > 0;
    } catch (error) {
      console.error("Error checking marketplace seller:", error);
      return false;
    }
  };

  const createMarketplaceSeller = async () => {
    const token = await getTokenSilently();
    const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

    const mutation = `
      mutation CreateMarketplaceSeller($fields: CreateMarketplace_sellerFieldsInput) {
        createMarketplace_seller(input: { fields: $fields }) {
          marketplace_seller {
            objectId
          }
        }
      }
    `;

    const fields = {
      name: org.nome,
      phone: org.telefono1 || org.cellulare1,
      enabled: true,
      public: false,
      b2b: false,
      b2c: false,
      public_prices: false,
      org_pointer: {
        link: org.objectId,
      },
    };

    try {
      const response = await axios.post(
        graphqlUrl,
        {
          query: mutation,
          variables: { fields },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data.createMarketplace_seller;
    } catch (error) {
      console.error("Error creating marketplace seller:", error);
      throw error;
    }
  };

  const updateMarketplaceSellerEnabled = async (enabled) => {
    const token = await getTokenSilently();
    const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

    // First get the marketplace_seller objectId
    const queryGetSeller = `
      query GetMarketplaceSeller($orgId: ID!) {
        marketplace_sellers(where: { org_pointer: { have: { objectId: { equalTo: $orgId } } } }) {
          edges {
            node {
              objectId
            }
          }
        }
      }
    `;

    try {
      const getResponse = await axios.post(
        graphqlUrl,
        {
          query: queryGetSeller,
          variables: { orgId: org.objectId },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const sellers = getResponse.data.marketplace_sellers.edges;
      if (sellers && sellers.length > 0) {
        const sellerId = sellers[0].node.objectId;

        // Update the enabled field
        const mutation = `
          mutation UpdateMarketplaceSeller($id: ID!, $fields: UpdateMarketplace_sellerFieldsInput) {
            updateMarketplace_seller(input: { id: $id, fields: $fields }) {
              marketplace_seller {
                objectId
                enabled
              }
            }
          }
        `;

        await axios.post(
          graphqlUrl,
          {
            query: mutation,
            variables: {
              id: sellerId,
              fields: { enabled },
            },
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
    } catch (error) {
      console.error("Error updating marketplace seller enabled status:", error);
      throw error;
    }
  };

  const handleChange = async (newChecked) => {
    setLoading(true);
    setChecked(newChecked); // Update local state immediately for visual feedback

    try {
      // Update the marketplace field in organizzazione
      const fields = [
        {
          key: "marketplace",
          value: newChecked,
        },
      ];

      // Don't pass messageApi to avoid double loading messages
      await updateOrgDetails(
        getTokenSilently,
        config,
        isProd,
        org.objectId,
        fields
      );

      // If enabling marketplace, check and create marketplace_seller record
      if (newChecked) {
        const exists = await checkMarketplaceSellerExists(org.objectId);

        if (!exists) {
          await createMarketplaceSeller();
          message.success("Organizzazione aggiunta al marketplace");
        } else {
          // If record exists, make sure it's enabled
          await updateMarketplaceSellerEnabled(true);
          message.success("Marketplace attivato");
        }
      } else {
        // If disabling marketplace, set enabled to false in marketplace_seller
        await updateMarketplaceSellerEnabled(false);
        message.success("Marketplace disattivato");
      }
    } catch (error) {
      console.error("Error updating marketplace status:", error);
      message.error("Errore durante l'aggiornamento");
      // Revert the toggle state on error
      setChecked(!newChecked);
    } finally {
      setLoading(false);
    }
  };

  return <Switch checked={checked} onChange={handleChange} loading={loading} />;
};

export default FieldMarketplace;
